import React, { useEffect, useRef, useState } from "react";
import NavigationBar from "../components/Nav/navigationBar";
import { FaHotel, FaUsers, FaStar } from "react-icons/fa";

export default function AboutUs() {
  // Hook to track which sections are visible
  const [visibleSections, setVisibleSections] = useState([]);
  const sectionsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => [...prev, entry.target.id]);
          }
        });
      },
      { threshold: 0.2 } // Trigger when 20% of section is visible
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => {
      sectionsRef.current.forEach((section) => {
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  const setSectionRef = (el, index) => {
    sectionsRef.current[index] = el;
  };

  const isVisible = (id) => visibleSections.includes(id);

  return (
   <div className="overflow-x-hidden pt-32">
      {/* Navigation */}
      <NavigationBar />

      {/* Hero Section */}
      <section
        id="hero"
        ref={(el) => setSectionRef(el, 0)}
        className={`relative bg-orange-200 py-32 transition-all duration-1000 ${
          isVisible("hero")
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-10"
        }`}
      >
        <img
          src="../assets/penukonda_fort.jpg"
          alt="Penukonda view"
          className="absolute inset-0 w-full h-full object-cover opacity-20 z-0"
        />
        <div className="relative z-10 text-center px-4 sm:px-6">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-800 mb-4">
            About Baba Fakruddin Room Booking
          </h1>
          <p className="text-lg sm:text-xl text-gray-700 max-w-2xl mx-auto">
            Making your stay in Penukonda comfortable and memorable.
          </p>
        </div>
      </section>

      {/* Company Info */}
      <section
        id="company"
        ref={(el) => setSectionRef(el, 1)}
        className={`py-20 px-4 sm:px-6 md:px-12 bg-white transition-all duration-1000 ${
          isVisible("company")
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-10"
        }`}
      >
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-8">Who We Are</h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            Baba Fakruddin Room Booking has been serving travelers in Penukonda
            for over a decade. We provide clean, comfortable, and affordable
            rooms with modern amenities and exceptional customer service.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section
        id="mission"
        ref={(el) => setSectionRef(el, 2)}
        className={`py-20 px-4 sm:px-6 md:px-12 bg-orange-50 transition-all duration-1000 ${
          isVisible("mission")
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-10"
        }`}
      >
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-8">Our Mission</h2>
          <p className="text-gray-700 text-lg leading-relaxed max-w-3xl mx-auto mb-12">
            To make every stay memorable by offering a seamless booking
            experience, top-notch facilities, and personalized service.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-8">
            <div className="flex flex-col items-center text-gray-800">
              <FaHotel className="text-4xl mb-2 text-orange-400" />
              <h3 className="font-semibold text-xl mb-1">Comfortable Rooms</h3>
              <p className="text-gray-600 text-center">
                Modern rooms with all essential amenities.
              </p>
            </div>
            <div className="flex flex-col items-center text-gray-800">
              <FaUsers className="text-4xl mb-2 text-orange-400" />
              <h3 className="font-semibold text-xl mb-1">Customer Service</h3>
              <p className="text-gray-600 text-center">
                Friendly staff always ready to help.
              </p>
            </div>
            <div className="flex flex-col items-center text-gray-800">
              <FaStar className="text-4xl mb-2 text-orange-400" />
              <h3 className="font-semibold text-xl mb-1">Best Experience</h3>
              <p className="text-gray-600 text-center">
                We ensure a memorable and hassle-free stay.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section
        id="values"
        ref={(el) => setSectionRef(el, 3)}
        className={`py-20 px-4 sm:px-6 md:px-12 bg-white transition-all duration-1000 ${
          isVisible("values")
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-10"
        }`}
      >
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-8">Our Values</h2>
          <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto mb-12">
            Honesty, transparency, and dedication. Every interaction reflects
            our commitment to quality and service.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
            <div className="bg-orange-50 p-6 rounded-xl shadow-md">
              <h3 className="font-bold text-lg text-gray-800 mb-2">
                Integrity
              </h3>
              <p className="text-gray-600 text-sm">
                We uphold honesty and trust in all our dealings.
              </p>
            </div>
            <div className="bg-orange-50 p-6 rounded-xl shadow-md">
              <h3 className="font-bold text-lg text-gray-800 mb-2">Quality</h3>
              <p className="text-gray-600 text-sm">
                Our rooms and services meet the highest standards.
              </p>
            </div>
            <div className="bg-orange-50 p-6 rounded-xl shadow-md">
              <h3 className="font-bold text-lg text-gray-800 mb-2">Service</h3>
              <p className="text-gray-600 text-sm">
                We provide excellent service with a personal touch.
              </p>
            </div>
            <div className="bg-orange-50 p-6 rounded-xl shadow-md">
              <h3 className="font-bold text-lg text-gray-800 mb-2">Comfort</h3>
              <p className="text-gray-600 text-sm">
                Ensuring every guest feels at home during their stay.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section
        id="cta"
        ref={(el) => setSectionRef(el, 4)}
        className={`py-16 bg-orange-200 text-center transition-all duration-1000 ${
          isVisible("cta")
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-10"
        }`}
      >
        <h2 className="text-3xl font-bold text-gray-800 mb-4">
          Ready to Book Your Stay?
        </h2>
        <p className="text-gray-700 mb-6">
          Explore our rooms and find the perfect place for your visit.
        </p>
        <a
          href="/"
          className="inline-block bg-orange-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-orange-500 transition"
        >
          Book Now
        </a>
      </section>
    </div>
  );
}
